import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Box, FormControlLabel, MenuItem, Select, Switch, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import TableChartIcon from '@mui/icons-material/TableChart';
import { environment } from '../../../environments/environment';
import {
    Bucket, BreakdownResponse, BreakdownRow, BreakdownSort, BucketCounts, Granularity, Scope,
    TimeseriesResponse, executionStatsService, isAbort, viewerTimeZone
} from '../../services/executionStatsService';
import { PanelError, PanelSkeleton, SectionCard } from '../Benchmark/BenchmarkPrimitives';
import { control, menu } from '../CaseWorkflow/filterStyles';
import { keepingNavState } from '../../utils/searchParamsNavigation';
import StatusTrendChart from './StatusTrendChart';
import StatsBreakdownTable from './StatsBreakdownTable';
import RunListDrawer from './RunListDrawer';
import { RunListQuery } from './RunList';
import {
    BUCKET_LABELS, BUCKET_ORDER, FAILURE_RATE_DEFINITION, NOT_TRACKED, TOTAL_DEFINITION, bucketDefinition,
    count, failureRate, percent, trackedFromNote
} from './executionStatsVocabulary';
import { DASHBOARD_RANGES, GRANULARITY_LABELS, rangeBounds, rangeFor } from './executionStatsWindows';
import { dsVar, ink } from '../../theme/tokens';

const SORTS: BreakdownSort[] = ['failed', 'failureRate', 'total', 'pendingReview'];

const Kpi: React.FC<{ label: string; value: number | null; definition: string; ink?: string; detail?: string }> = ({
    label, value, definition, ink: tone, detail
}) => (
    <Box className="rounded-xl border border-line bg-page/40 p-4">
        <Box className="flex items-center gap-1.5">
            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), fontWeight: 500 }}>{label}</Typography>
            <Tooltip title={definition} arrow placement="top">
                <InfoOutlinedIcon sx={{ fontSize: 13, color: dsVar('fg-muted'), cursor: 'help' }} />
            </Tooltip>
        </Box>
        {value === null ? (
            <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-muted'), mt: 1, fontStyle: 'italic' }}>{NOT_TRACKED}</Typography>
        ) : (
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: value > 0 ? (tone ? ink(tone) : dsVar('fg')) : dsVar('fg-muted'), lineHeight: 1.2, mt: 0.5 }}>
                {count(value)}
            </Typography>
        )}
        {detail && <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 0.25 }}>{detail}</Typography>}
    </Box>
);

/** The range's headline figures, in the order people ask about them. */
const KpiTiles: React.FC<{ totals: BucketCounts; staleAfterHours: number; runtimeStatusSince: string | null }> = ({
    totals, staleAfterHours, runtimeStatusSince
}) => {
    const rate = failureRate(totals);
    // A range reaching back before reviews were recorded can't total them.
    const reviewsFrom = trackedFromNote(runtimeStatusSince);
    return (
        <Box className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
            <Kpi label="Runs" value={totals.total} definition={TOTAL_DEFINITION} />
            <Kpi label={BUCKET_LABELS.success} value={totals.success} definition={bucketDefinition('success', staleAfterHours)} />
            <Kpi
                label={BUCKET_LABELS.failed}
                value={totals.failed}
                ink="#F87171"
                definition={`${bucketDefinition('failed', staleAfterHours)} ${FAILURE_RATE_DEFINITION}`}
                detail={rate === null ? undefined : `${percent(rate)} of finished runs`}
            />
            {(['pendingReview', 'stalled', 'running'] as Bucket[]).map((bucket) => (
                <Kpi
                    key={bucket}
                    label={BUCKET_LABELS[bucket]}
                    value={totals[bucket]}
                    ink={bucket === 'running' ? undefined : '#FBBF24'}
                    definition={bucketDefinition(bucket, staleAfterHours)}
                    detail={bucket === 'pendingReview' && totals[bucket] === null && reviewsFrom ? reviewsFrom : undefined}
                />
            ))}
        </Box>
    );
};

/**
 * Run statistics across every flow (or agent node) in the org.
 *
 * Laid out in the order people ask: how many, how many failed, when, and which
 * ones. Every filter lives in the query string so a view can be shared, and
 * every count in the table opens the runs behind it in a docked drawer.
 */
const ExecutionOverview: React.FC<{ onDrawerChange?: (open: boolean) => void }> = ({ onDrawerChange }) => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const nodeScopeEnabled = Boolean(environment.executionStatsNodeScope);

    const scope: Scope = nodeScopeEnabled && searchParams.get('scope') === 'node' ? 'node' : 'flow';
    const range = rangeFor(searchParams.get('range'));
    const requestedGranularity = searchParams.get('granularity') as Granularity | null;
    const granularity: Granularity = requestedGranularity && range.granularities.includes(requestedGranularity)
        ? requestedGranularity
        : range.defaultGranularity;
    const groupVersions = searchParams.get('versions') !== 'separate';
    const sortParam = searchParams.get('sort') as BreakdownSort | null;
    const sort: BreakdownSort = sortParam && SORTS.includes(sortParam) ? sortParam : 'failed';

    const patch = useCallback((changes: Record<string, string | null>) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value); else next.delete(key);
        });
        setSearchParams(next, keepingNavState(location.state, { replace: true }));
    }, [searchParams, setSearchParams, location.state]);

    const [bounds, setBounds] = useState(() => rangeBounds(range));
    const [trend, setTrend] = useState<TimeseriesResponse | null>(null);
    const [breakdown, setBreakdown] = useState<BreakdownResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [drawer, setDrawer] = useState<{ row: BreakdownRow; query: RunListQuery } | null>(null);
    const tz = useMemo(() => viewerTimeZone(), []);

    useEffect(() => { onDrawerChange?.(Boolean(drawer)); }, [drawer, onDrawerChange]);

    useEffect(() => {
        const controller = new AbortController();
        const next = rangeBounds(range);
        setBounds(next);
        setTrend(null);
        setBreakdown(null);
        setError(null);
        const selection = { scope, keyType: 'code' as const, groupVersions, ...next };
        Promise.all([
            executionStatsService.getTimeseries({ ...selection, granularity, tz }, { signal: controller.signal }),
            executionStatsService.getBreakdown({ ...selection, sort, limit: 100 }, { signal: controller.signal })
        ])
            .then(([trendRes, breakdownRes]) => {
                setTrend(trendRes);
                setBreakdown(breakdownRes);
            })
            .catch((err) => { if (!isAbort(err)) setError(err?.message || 'Could not load run statistics.'); });
        return () => controller.abort();
        // `range` is derived from its key; the key is the dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope, range.key, granularity, groupVersions, sort, tz, attempt]);

    const openRuns = (row: BreakdownRow, bucket: Bucket | null) => {
        setDrawer({
            row,
            query: { scope, keyType: 'code', key: row.key, groupVersions, from: bounds.from, to: bounds.to, bucket }
        });
    };

    const trackedBuckets = breakdown
        ? BUCKET_ORDER.filter((b) =>
            (b === 'pendingReview' && breakdown.tracked.pendingReview)
            || ((b === 'running' || b === 'stalled') && breakdown.tracked.running)
            || b === 'success' || b === 'failed')
        : BUCKET_ORDER;

    const noun = scope === 'flow' ? 'workflow' : 'agent';

    return (
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
                <Box sx={{ maxWidth: '84rem', mx: 'auto', px: 4, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Filters, in one row above everything they change. */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                        {nodeScopeEnabled && (
                            <Select size="small" value={scope} onChange={(e) => patch({ scope: e.target.value === 'node' ? 'node' : null })} sx={control} MenuProps={menu}>
                                <MenuItem value="flow">Workflows</MenuItem>
                                <MenuItem value="node">Agent nodes</MenuItem>
                            </Select>
                        )}
                        <Select
                            size="small"
                            value={range.key}
                            onChange={(e) => patch({ range: String(e.target.value), granularity: null })}
                            sx={control}
                            MenuProps={menu}
                        >
                            {DASHBOARD_RANGES.map((r) => <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>)}
                        </Select>
                        <Select
                            size="small"
                            value={granularity}
                            onChange={(e) => patch({ granularity: String(e.target.value) })}
                            sx={{ ...control, minWidth: 110 }}
                            MenuProps={menu}
                        >
                            {range.granularities.map((g) => <MenuItem key={g} value={g}>By {GRANULARITY_LABELS[g].toLowerCase()}</MenuItem>)}
                        </Select>
                        <Tooltip title="Count every published version of a workflow together, instead of one row per version." arrow>
                            <FormControlLabel
                                sx={{ ml: 0.5, '.MuiFormControlLabel-label': { fontSize: '0.8125rem', color: dsVar('fg-soft') } }}
                                control={
                                    <Switch
                                        size="small"
                                        checked={groupVersions}
                                        onChange={(e) => patch({ versions: e.target.checked ? null : 'separate' })}
                                    />
                                }
                                label="Group versions"
                            />
                        </Tooltip>
                    </Box>

                    {error ? (
                        <PanelError message={error} onRetry={() => setAttempt((n) => n + 1)} />
                    ) : (
                        <>
                            {trend ? (
                                <KpiTiles
                                    totals={trend.totals}
                                    staleAfterHours={trend.staleAfterHours}
                                    runtimeStatusSince={trend.runtimeStatusSince ?? null}
                                />
                            ) : (
                                <PanelSkeleton height={96} />
                            )}

                            <SectionCard
                                title={`Runs by ${granularity}`}
                                subtitle={`${range.label}, by outcome`}
                                icon={<InsightsIcon fontSize="small" />}
                            >
                                {trend ? <StatusTrendChart data={trend} /> : <PanelSkeleton height={260} />}
                            </SectionCard>

                            <SectionCard
                                title={`By ${noun}`}
                                subtitle={`${range.label} · click a count to see its runs`}
                                icon={<TableChartIcon fontSize="small" />}
                            >
                                {breakdown ? (
                                    <StatsBreakdownTable
                                        data={breakdown}
                                        sort={sort}
                                        onSort={(next) => patch({ sort: next === 'failed' ? null : next })}
                                        onSelect={openRuns}
                                    />
                                ) : (
                                    <PanelSkeleton height={200} />
                                )}
                            </SectionCard>
                        </>
                    )}
                </Box>
            </Box>

            <RunListDrawer
                open={Boolean(drawer)}
                onClose={() => setDrawer(null)}
                title={drawer ? drawer.row.name || drawer.row.key : ''}
                query={drawer?.query ?? null}
                buckets={trackedBuckets}
                onBucketChange={(bucket) => setDrawer((d) => (d ? { ...d, query: { ...d.query, bucket } } : d))}
            />
        </Box>
    );
};

export default ExecutionOverview;
